<div class="flex flex-col h-[calc(100vh-56px)] overflow-hidden bg-wb-bg text-wb-text">

  <app-readonly-banner />
  <app-save-error-overlay />

  <div class="flex flex-1 min-h-0 overflow-hidden">

    <!-- Left: Media Pool -->
    <aside class="shrink-0 flex flex-col overflow-hidden bg-wb-elevated" [style.width.px]="layoutService.leftWidth()">
      <div class="flex-1 min-h-0 overflow-y-auto overflow-x-auto">
        <app-file-browser
          [groupedFiles]="filesService.groupedFiles()"
          [loading]="filesService.loading()"
          [projectSlug]="slug"
          (variantGroupChanged)="onVariantGroupChanged($event)"
          (timelineChanged)="onTimelineChangedExternally()"
          (mediaDeleted)="onMediaDeleted($event)"
        />
      </div>
    </aside>

    <!-- Left resize handle -->
    <div class="resize-handle" (mousedown)="layoutService.startResize('left', $event)"></div>

    <!-- Center: Player + Timeline -->
    <section class="flex-1 min-w-0 flex flex-col overflow-hidden">
      <!-- Player (snaps to the video's natural aspect-ratio size; shrinks,
           letterboxed, once the timeline below claims its min height) -->
      <div class="player-container overflow-hidden flex items-end shrink min-h-0">
        <app-player-panel
          [slug]="slug"
          [signatureUuid]="projectId"
          [origCoordsWidth]="previewOrigCoordsWidth()"
          [origCoordsHeight]="previewOrigCoordsHeight()"
          [hideOsdTimeline]="true"
          [generationState]="playerGenerationState()"
          [generationJobs]="waitingMediaJobs()"
          (viewReady)="onPlayerViewReady()"
          (currentTimeMs)="currentTimeMs.set($event)"
          (activeChapter)="onActiveChapterChange($event)"
        />
      </div>

      <!-- Timeline / Graph (directly after player, claims all leftover height
           down to its min-height floor — drag the handle to raise that floor) -->
      @if (projectData()) {
        <div class="resize-handle-h" (mousedown)="layoutService.startResize('bottom', $event)"></div>
        <div class="timeline-glass-panel flex flex-col overflow-hidden flex-1" [style.min-height.px]="layoutService.timelineMinHeight()">
          <div class="flex items-center gap-0 shrink-0 px-2 pt-2 border-b border-wb-border">
            <button class="tab-btn" [class.tab-btn--active]="bottomPanelTab() === 'timeline'" (click)="bottomPanelTab.set('timeline')">Timeline</button>
            <button class="tab-btn" [class.tab-btn--active]="bottomPanelTab() === 'graph'" (click)="bottomPanelTab.set('graph')">Graph</button>
            @if (chapterOptions().length) {
              <p-select
                [options]="chapterOptions()"
                [ngModel]="selectedChapterIndex()"
                (ngModelChange)="onChapterDropdownChange($event)"
                optionLabel="label"
                optionValue="value"
                appendTo="body"
                styleClass="bottom-panel-chapter-select"
                placeholder="Chapter"
              />
            }
          </div>
          <div class="flex-1 min-h-0 overflow-hidden">
            @if (bottomPanelTab() === 'timeline') {
              <app-timeline-panel
                [projectData]="projectData()"
                [slug]="slug"
                [activeSceneSlug]="activeSceneSlug()"
                [activeAudioSlug]="activeAudioSlug()"
                [argsMap]="currentArgsMap()"
                [selectedChapterIndex]="selectedChapterIndex()"
                [currentTimeMs]="currentTimeMs()"
                [refreshKey]="timelineVersion()"
                (projectDataChange)="onTimelineDataChange($event)"
                (sceneSelect)="selectSceneFromForm($event)"
                (audioSelect)="selectAudioFromTimeline($event)"
                (seekTo)="onSeek($event)"
                (fileDrop)="onTimelineFileDrop($event)"
                (sceneRemove)="onTimelineSceneRemove($event)"
                (audioRemove)="onTimelineAudioRemove($event)"
              />
            } @else {
              <app-graph-panel
                [projectData]="projectData()"
                [slug]="slug"
                [activeSceneSlug]="activeSceneSlug()"
                [argsMap]="currentArgsMap()"
                [selectedChapterIndex]="selectedChapterIndex()"
                (sceneSelect)="selectSceneFromForm($event)"
                (chapterSelect)="onChapterSelectFromGraph($event)"
              />
            }
          </div>
        </div>
      }
    </section>

    <!-- Right resize handle -->
    <div class="resize-handle" (mousedown)="layoutService.startResize('right', $event)"></div>

    <!-- Right: Toolbar + Breadcrumb + Form/JSON Editor -->
    <section class="shrink-0 flex flex-col overflow-hidden bg-wb-elevated" [style.width.px]="layoutService.rightWidth()">
      <!-- Header toolbar -->
      <div class="flex items-center gap-2 px-3 py-1.5 bg-wb-surface min-h-[28px] shrink-0">
        @if (selectedPath()) {
          <div class="view-toggle shrink-0">
            <button class="view-toggle-btn" [class.view-btn-active]="viewMode() === 'form'" (click)="switchToForm()">Form</button>
            <button class="view-toggle-btn" [class.view-btn-active]="viewMode() === 'json'" (click)="viewMode.set('json')">JSON</button>
          </div>
        }
        <div class="flex-1"></div>
        @if (!isImportedProject()) {
          <button class="save-btn shrink-0" (click)="saveFile()" [disabled]="editorSaving() || !globalDirty()">
            @if (editorSaving()) { Saving... }
            @else if (globalDirty()) { Save ({{ pendingCount() }}) }
            @else { Saved }
          </button>
          <button
            class="autosave-toggle-btn shrink-0"
            [class.autosave-toggle-btn--active]="stateService.autosaveEnabled()"
            (click)="stateService.autosaveEnabled.set(!stateService.autosaveEnabled())"
            [title]="stateService.autosaveEnabled() ? 'Autosave: on (click to disable)' : 'Autosave: off (click to enable)'"
          >
            <i class="pi pi-bolt text-sm"></i>
          </button>
        }
        @if (!isImportedProject()) {
          <button class="config-toggle-btn shrink-0" (click)="maintenanceMenu.toggle($event)" [attr.aria-haspopup]="true" title="Maintenance">
            <i class="pi" [class.pi-wrench]="!generatingMissingAudioCodecs() && !repairingBrokenImageBaseUrls()" [class.pi-spin]="generatingMissingAudioCodecs() || repairingBrokenImageBaseUrls()" [class.pi-spinner]="generatingMissingAudioCodecs() || repairingBrokenImageBaseUrls()"></i>
          </button>
          <p-menu #maintenanceMenu [model]="maintenanceMenuItems()" [popup]="true" appendTo="body" styleClass="export-menu-popup" />
        }
        <button class="config-toggle-btn shrink-0 relative" (click)="jobsDialogVisible.set(true)" title="Generation jobs">
          <i class="pi pi-list text-sm"></i>
          @if (jobsService.activeCount() > 0) {
            <span class="jobs-badge">{{ jobsService.activeCount() }}</span>
          }
        </button>
        <button class="config-toggle-btn shrink-0" (click)="saveHistoryVisible.set(true)" title="Save history">
          <i class="pi pi-history text-sm"></i>
        </button>
        <button class="config-toggle-btn shrink-0" [class.config-toggle-btn--active]="configPanelOpen()" (click)="configPanelOpen.set(!configPanelOpen())" title="Configuration">
          <i class="pi pi-cog text-sm"></i>
        </button>
        <button class="config-toggle-btn shrink-0 relative" (click)="moreMenu.toggle($event)" [attr.aria-haspopup]="true" title="More">
          <i class="pi pi-ellipsis-v text-sm"></i>
          @if (exportBusy()) {
            <span class="jobs-badge">{{ exportProgressLabel() }}</span>
          } @else if (publishBusy()) {
            <span class="jobs-badge">{{ publishProgress() }}</span>
          }
        </button>
        <p-menu #moreMenu [model]="moreMenuItems()" [popup]="true" appendTo="body" styleClass="export-menu-popup" />
      </div>
      <!-- Breadcrumb -->
      <div class="flex items-center px-3 pt-3 pb-1 shrink-0 text-[0.75rem]">
        <app-breadcrumb [slug]="slug" [selectedPath]="selectedPath()" [selectedNodeType]="selectedNodeType()" (navigate)="onBreadcrumbClick($event)" />
      </div>

      <div class="flex-1 min-h-0 overflow-y-auto flex flex-col">
        @if (fileLoading()) {
          <div class="flex-1 flex items-center justify-center text-wb-muted"><p>Loading...</p></div>
        }

        <!-- Monaco editor -->
        <div class="monaco-wrapper flex-1 min-h-[200px]" [class.hidden]="fileLoading() || viewMode() === 'form'">
          <ngx-monaco-editor
            [options]="editorOptions()"
            [ngModel]="editorContent()"
            (ngModelChange)="onEditorChange($event)"
          />
        </div>

        <!-- Form editor -->
        @if (viewMode() === 'form' && !fileLoading() && formData()) {
          <fieldset #formScroller cdkScrollable class="flex-1 min-h-0 overflow-y-auto p-3 min-w-0 border-0 readonly-fieldset"
            [class.readonly-fieldset--locked]="isImportedProject()"
            [disabled]="isImportedProject()">
            @switch (selectedNodeType()) {
              @case ('project') {
                <app-project-form [data]="$any(formData())" (dataChange)="onFormChange($event)" (chapterSelect)="selectChapter($event.chapterSlug)" (imageSelect)="selectImageFromForm($event)" (sceneSelect)="selectSceneFromForm($event)" (copyScene)="onCopyScene($event)" (copyImage)="onCopyImage($event)" />
              }
              @case ('chapter') {
                <app-chapter-form [data]="$any(formData())" [projectSlug]="slug" [namesMapKeys]="projectNamesMapKeys()" [argKeys]="projectArgKeys()" (dataChange)="onChapterFormChange($event)" (slugRenamed)="onChapterSlugRenamed($event)" (sceneSelect)="selectSceneFromForm($event)" (imageSelect)="selectImageFromForm($event)" (namesMapPopulate)="onNamesMapPopulate($event)" (copyScene)="onCopyScene($event)" (copyImage)="onCopyImage($event)" />
              }
              @case ('scene') {
                <app-scene-form [data]="$any(formData())" [scenePath]="selectedPath() ?? ''" [projectFps]="projectData()?.fps ?? 25" [aeComp]="aeComp()" [sceneRefAttributes]="currentSceneRefAttributes()" [sceneRefVariant]="currentSceneRefVariant()" [sceneRefAlt]="currentSceneRefAlt()" [argKeys]="projectArgKeys()" [siblingSlug]="currentSceneSiblingSlugs()" [chapters]="chapters()" (dataChange)="onFormChange($event)" (slugRenamed)="onSceneSlugRenamed($event)" (requestSave)="saveFile()" (copyItem)="onCopyContentItem($event)" (copyAction)="onCopyAction($event)" (sceneRefAttributesChange)="onSceneRefAttributesChange($event)" (sceneRefVariantChange)="onSceneRefVariantChange($event)" (sceneRefAltChange)="onSceneRefAltChange($event)" />
              }
              @case ('image') {
                <app-image-form [data]="$any(formData())" [aeComp]="aeComp()" [imagePath]="selectedPath() ?? ''" [imageRef]="currentImageRef()" [availableWidths]="projectAvailableWidths()" (dataChange)="onFormChange($event)" (imageRefDimensionsChange)="onImageRefDimensionsChange($event)" (slugRenamed)="onImageSlugRenamed($event)" (requestSave)="saveFile()" (copyItem)="onCopyContentItem($event)" (previewRequest)="onImagePreviewRequest()" />
              }
            }
          </fieldset>
        }
      </div>
    </section>

  <!-- Config panel slide-over -->
  @if (configPanelOpen()) {
    <div class="config-overlay" (click)="configPanelOpen.set(false)">
      <div class="config-slide-panel" (click)="$event.stopPropagation()">
        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-2">
            <span class="text-sm font-bold text-wb-accent uppercase tracking-wider">Configuration</span>
            @if (configService.saved()) {
              <span class="config-saved-badge">Saved</span>
            }
          </div>
          <button class="text-wb-muted hover:text-wb-text transition-colors bg-transparent border-0 cursor-pointer" (click)="configPanelOpen.set(false)">
            <i class="pi pi-times"></i>
          </button>
        </div>
        <div class="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
          <app-config-panel
            [meta]="configService.meta()"
            [chapters]="configService.chapters()"
            [argDefs]="configService.argDefs()"
            [varDefs]="configService.varDefs()"
            [args]="configService.args()"
            [vars]="configService.vars()"
            [region]="configService.region()"
            [saved]="configService.saved()"
            (regionChange)="configService.region.set($event)"
            (chaptersChange)="configService.chapters.set($event)"
            (argChange)="configService.setArgValue($event.index, $event.value)"
            (varChange)="configService.setVarValue($event.index, $event.value)"
          />
        </div>
      </div>
    </div>
  }

  </div>

</div>

<app-jobs-dialog [visible]="jobsDialogVisible()" (visibleChange)="jobsDialogVisible.set($event)" />
<app-save-history-dialog [(visible)]="saveHistoryVisible" [projectSlug]="slug" />
<app-copy-target-dialog
  [(visible)]="copyDialogVisible"
  [project]="projectData()"
  [sourceLabel]="copySourceLabel()"
  (targetSelected)="onCopyTargetSelected($event)"
/>
<app-copy-target-dialog
  [(visible)]="copyActionDialogVisible"
  [project]="projectData()"
  [sourceLabel]="copyActionSourceLabel()"
  header="Copy action"
  [targetKinds]="sceneOnlyTargetKinds"
  (targetSelected)="onCopyActionTargetSelected($event)"
/>
<app-copy-chapter-dialog
  [(visible)]="copyChapterDialogVisible"
  [project]="projectData()"
  [sourceLabel]="copyEntitySourceLabel()"
  [header]="copyChapterDialogHeader()"
  (targetSelected)="onCopyChapterTargetSelected($event)"
/>
<app-publish-mode-dialog
  [(visible)]="publishModeDialogVisible"
  [slug]="slug"
  [published]="publishState() === 'done'"
  (confirmed)="publishToS3($event)"
/>
<app-personalized-link-dialog
  [(visible)]="personalizedLinkDialogVisible"
  [slug]="slug"
  [argDefs]="configService.argDefs()"
  [varDefs]="configService.varDefs()"
  [vars]="configService.vars()"
  [namesMap]="projectNamesMap()"
/>
<app-image-preview-dialog
  [(visible)]="imagePreviewVisible"
  [request]="imagePreviewRequest()"
/>
<p-toast position="bottom-right" />
